import { useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import ButtonWithLoading from "components/ButtonWithLoading";
import apiRequest from "utils/apiRequestWithErrorCode";
import ApplicationStatus, { SyncStatus } from "./ApplicationStatus";
import { StorageSettingsUpdate } from "../types";
import { StorageSettings, TestSourceResponse } from "@dashboard-v3/api";
import { initOffice365Auth } from "pages/Accounts/Buttons/OfficeSourceAuth";
import { Box, Button } from "@material-ui/core";

interface Props {
  settings: StorageSettings;
  onAccountSync: (update: StorageSettingsUpdate) => void;
}

export default function OfficeDirectorySync({
  settings,
  onAccountSync,
}: Props) {
  const { t } = useTranslation("storage");
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const { office365 } = settings;

  const status = office365.directory
    ? SyncStatus.INSTALLED
    : SyncStatus.NOT_INSTALLED;

  const onSourceAdd = () => {
    history.push({
      pathname: "/accounts",
      state: { action: "ADD", type: "OFFICE365" },
    });
  };

  const onReinstall = async () => {
    const reInstalled = await initOffice365Auth();
    if (reInstalled) {
      enqueueSnackbar(t("officeDirectorySync.reinstall.ok"), {
        variant: "success",
      });
    } else {
      enqueueSnackbar(t("officeDirectorySync.reinstall.reject"));
    }
  };

  const onVerify = async () => {
    setLoading(true);
    try {
      const res = await apiRequest<TestSourceResponse>(
        "GET",
        `/sources/OFFICE365/verify`
      );

      onAccountSync({
        ...settings,
        office365: { ...settings.office365, directory: res.sucess },
      });

      if (res.sucess) {
        enqueueSnackbar(t("officeDirectorySync.verify.ok"), {
          variant: "success",
        });
      } else {
        enqueueSnackbar(t("officeDirectorySync.verify.failed"), {
          variant: "error",
        });
      }
    } catch (e) {
      enqueueSnackbar(t("officeDirectorySync.verify.unknownError"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" gridGap="1rem" alignItems="center">
      <ApplicationStatus status={status} />
      {status === SyncStatus.NOT_INSTALLED && (
        <ButtonWithLoading
          variant="text"
          color="primary"
          loading={loading}
          onClick={onSourceAdd}
        >
          {t("officeDirectorySync.addSource")}
        </ButtonWithLoading>
      )}
      {status === SyncStatus.INSTALLED && (
        <>
          <Button variant="text" color="primary" onClick={onReinstall}>
            {t("reInstall")}
          </Button>

          <ButtonWithLoading
            variant="text"
            color="primary"
            loading={loading}
            onClick={onVerify}
          >
            {t("verify")}
          </ButtonWithLoading>
        </>
      )}
    </Box>
  );
}
